import { Clock, BarChart, MousePointerClick, CheckCircle, AlertTriangle } from "lucide-react"
import { Card, CardContent } from "@/components/ui/card"
import { cn } from "@/lib/utils"
import { StatIcon } from "@/types/enums"

interface StatMetricCardProps {
  title: React.ReactNode
  value: string | number
  trend?: {
    value: number
    label: string
    isPositive?: boolean
  }
  icon: StatIcon
  className?: string
  onClick?: () => void
}

const StatMetricCard = ({ title, value, trend, icon, className, onClick }: StatMetricCardProps) => {
  const getIcon = () => {
    switch (icon) {
      case StatIcon.HOURS:
        return <Clock className="h-5 w-5 text-muted-foreground" />
      case StatIcon.PRODUCTIVITY:
        return <BarChart className="h-5 w-5 text-muted-foreground" />
      case StatIcon.APPROVAL:
        return <MousePointerClick className="h-5 w-5 text-muted-foreground" />
      case StatIcon.CHECK:
        return <CheckCircle className="h-5 w-5 text-muted-foreground" />
      case StatIcon.ALERT:
        return <AlertTriangle className="h-5 w-5 text-muted-foreground" />
      default:
        return null
    }
  }

  const getTrendIndicator = () => {
    if (!trend) return null
    
    let color = "text-gray-500"
    let prefix = ""
    
    if (trend.isPositive !== undefined) {
      color = trend.isPositive ? "text-green-600" : "text-red-600"
      prefix = trend.isPositive ? "+" : ""
    }
    
    return (
      <p className={cn("text-sm mt-1 whitespace-nowrap truncate", color)}>
        {prefix}{trend.value}% {trend.label}
      </p>
    )
  }
  
  return (
    <Card 
      className={cn(
        "overflow-hidden transition-all duration-200 hover:shadow-lg", 
        "shadow-sm border-2 border-gray-200 dark:border-gray-600", 
        "dark:shadow-md dark:hover:shadow-xl",
        "hover:border-gray-300 dark:hover:border-gray-500",
        "bg-card dark:bg-card",
        "w-full h-full flex flex-col justify-center",
        onClick && "cursor-pointer",
        className
      )}
      onClick={onClick}
    >
      <CardContent className="p-6 flex flex-col justify-center h-full">
        <div className="flex items-center gap-2 mb-2 text-sm text-muted-foreground">
          {getIcon()}
          {title}
        </div>
        
        <div className="flex flex-col">
          <span className="text-3xl font-bold">{value}</span>
          {getTrendIndicator()}
        </div>
      </CardContent>
    </Card>
  )
}

export default StatMetricCard
